import React, { useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { IStripePaymentMethod } from "@eagerdog/stripe";
import { Config } from "@eagerdog/config";

import AddUserPaymentMethod from "src/components/AddUserPaymentMethod";
import Button from "src/components/Button/Button";

import "./CardOnFile.scss";

interface IProps {
  paymentMethod: IStripePaymentMethod;
  customerId: string;
  onCardUpdated: (methods: IStripePaymentMethod[]) => void;
}

const BRAND_LABELS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  unknown: "Card",
};

const stripePromise = loadStripe(Config.web.REACT_APP_STRIPE_PUBLIC_KEY ?? "");

const CardOnFile: React.FC<IProps> = ({ paymentMethod, customerId, onCardUpdated }) => {
  const [editing, setEditing] = useState(false);

  const card = paymentMethod.card;
  if (!card) return null;

  const brand = BRAND_LABELS[card.brand?.toLowerCase()] ?? card.brand;
  const last4 = card.last4;
  const expMonth = String(card.exp_month).padStart(2, "0");
  const expYear = String(card.exp_year).slice(-2);

  return (
    <div className="card-on-file">
      {!editing ? (
        <div className="card-on-file__display">
          <div className="card-on-file__icon">
            <CardIcon brand={card.brand} />
          </div>
          <div className="card-on-file__details">
            <span className="card-on-file__brand">{brand}</span>
            <span className="card-on-file__number">&nbsp;&bull;&bull;&bull;&bull; {last4}</span>
            <span className="card-on-file__expiry">Exp. {expMonth}/{expYear}</span>
          </div>
          <Button
            variant="secondary"
            className="card-on-file__edit-btn"
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        </div>
      ) : (
        <div className="card-on-file__edit">
          <div className="card-on-file__edit-header">
            <span className="card-on-file__edit-title">Update Card</span>
            <button
              className="card-on-file__cancel"
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </div>
          <Elements stripe={stripePromise}>
            <AddUserPaymentMethod
              customerId={customerId}
              hideTitle
              onSave={async () => {
                const { apiService } = await import("src/services");
                const methods = await apiService.getSelfPaymentMethods();
                onCardUpdated(methods);
                setEditing(false);
              }}
            />
          </Elements>
        </div>
      )}
    </div>
  );
};

const CardIcon: React.FC<{ brand: string }> = ({ brand }) => {
  const b = brand?.toLowerCase();

  const colors: Record<string, { bg: string; text: string }> = {
    visa:       { bg: "#1a1f71", text: "#ffffff" },
    mastercard: { bg: "#eb001b", text: "#ffffff" },
    amex:       { bg: "#2e77bc", text: "#ffffff" },
    discover:   { bg: "#ff6600", text: "#ffffff" },
  };

  const style = colors[b] ?? { bg: "#888", text: "#fff" };
  const label = b === "visa" ? "VISA"
    : b === "mastercard" ? "MC"
    : b === "amex" ? "AMEX"
    : b === "discover" ? "DISC"
    : brand.slice(0, 4).toUpperCase();

  return (
    <span
      className="card-on-file__brand-badge"
      style={{ background: style.bg, color: style.text }}
    >
      {label}
    </span>
  );
};

export default CardOnFile;
